/**
 * Pandit verification — `/v1/admin/kyc*`.
 *
 * **Six badges, decided independently.** There is no "approve the pandit" call that lights
 * everything up: identity can pass while bank fails, and the profile then says exactly that. Each
 * badge names the evidence that justified it — an approved document, two positive references, a
 * clear background check — and the server validates that evidence is in its passing state *at the
 * moment of issue*, not when it was uploaded.
 *
 * **Approving the case is a separate act from issuing the badges**, and the server refuses it until
 * the two matching-mandatory badges exist. It deliberately does not mint them as a side effect: a
 * badge is a statement about specific evidence, and creating one because a case was approved would
 * invert that.
 *
 * **A document view is audited before the URL is handed over.** `useViewDocument` is a mutation
 * rather than a query for that reason — it has a side effect on the server, and caching it would
 * both hide accesses from the audit trail and hand back an expired presigned link.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type QueueRow = components['schemas']['QueueRow'];
export type CaseResponse = components['schemas']['AdminCaseResponse'];
export type DocumentRow = components['schemas']['AdminDocumentRow'];
export type BadgeRow = components['schemas']['AdminBadgeRow'];
type QueueResponse = components['schemas']['QueueResponse'];
type ViewUrl = components['schemas']['ViewUrlResponse'];
export type RejectionTemplate = components['schemas']['RejectionTemplateRow'];
type AuditList = components['schemas']['AuditListResponse'];
type RejectionTemplatePatch = components['schemas']['RejectionTemplatePatch'];

export interface QueueQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  status?: string;
  city_id?: number;
}

/** Exported for its own test — an absent filter is omitted, never sent empty. */
export function queueQueryString(params: QueueQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.status) search.set('status', params.status);
  if (params.city_id !== undefined) search.set('city_id', String(params.city_id));
  return search.toString();
}

export function useKycQueue(params: QueueQuery, enabled = true) {
  return useQuery({
    queryKey: keys.kyc.queue(params),
    queryFn: () => authedClient.request<QueueResponse>(`/v1/admin/kyc/queue?${queueQueryString(params)}`),
    enabled,
  });
}

export function useKycCase(caseId: number | undefined) {
  return useQuery({
    queryKey: keys.kyc.case(caseId ?? 0),
    queryFn: () => authedClient.request<CaseResponse>(`/v1/admin/kyc/cases/${caseId}`),
    enabled: caseId !== undefined,
  });
}

/**
 * The case History tab (05-admin/06 §4 step 9, R310): every `kyc.*` act on this pandit, newest first.
 *
 * Its own read and not the audit log's, because `supply_kyc` works these cases and does not hold
 * the audit-log read; this one can only ever return this pandit's KYC rows. Keyset-paged like the
 * log, so a row written between two pages never shows twice.
 */
export function useKycHistory(panditId: number) {
  return useInfiniteQuery({
    queryKey: keys.kyc.history(panditId),
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      authedClient.request<AuditList>(
        `/v1/admin/pandits/${panditId}/kyc-history?limit=50${pageParam === undefined ? '' : `&cursor=${pageParam}`}`,
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Invalidates everything: a document decision moves the queue counts and the case detail. */
function useKycMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.kyc.all() }),
  });
}

export function useReviewDocument() {
  return useKycMutation(
    ({ documentId, decision, reason }: { documentId: number; decision: string; reason?: string }) =>
      authedClient.request<DocumentRow>(`/v1/admin/kyc/documents/${documentId}/review`, {
        method: 'POST',
        body: { decision, rejection_reason: reason || null },
      }),
  );
}

/**
 * A mutation, not a query. Every mint writes an audit row before the URL is returned — "who looked
 * at this person's identity papers, and when" is a question both a regulator and the pandit are
 * entitled to ask — and the link is short-lived, so a cached one would be dead by the second click.
 */
export function useViewDocument() {
  return useMutation({
    mutationFn: (documentId: number) =>
      authedClient.request<ViewUrl>(`/v1/admin/kyc/documents/${documentId}/view`),
  });
}

export function useIssueBadge() {
  return useKycMutation((body: components['schemas']['BadgeIssueIn']) =>
    authedClient.request<BadgeRow>('/v1/admin/kyc/badges', { method: 'POST', body }),
  );
}

/**
 * Revoking one badge never touches another.
 *
 * That independence is the requirement, not an implementation detail: a lapsed background check
 * must not quietly un-verify somebody's bank account. `AdminCaseResponse.badges` now carries the
 * id this needs (CSR-026 closed) — before that it did not, and this hook was deleted rather than
 * left uncalled.
 */
export function useRevokeBadge() {
  return useKycMutation(({ badgeId, status, reason }: { badgeId: number; status: string; reason: string }) =>
    authedClient.request<BadgeRow>(`/v1/admin/kyc/badges/${badgeId}/revoke`, {
      method: 'POST',
      body: { status, reason },
    }),
  );
}

export function useDecideCase() {
  return useKycMutation(({ caseId, decision, reason }: { caseId: number; decision: string; reason?: string }) =>
    authedClient.request<components['schemas']['CaseDecisionResponse']>(
      `/v1/admin/kyc/cases/${caseId}/decide`,
      { method: 'POST', body: { decision, rejection_reason: reason || null } },
    ),
  );
}

export function useReopenCase() {
  return useKycMutation(({ caseId, reason }: { caseId: number; reason: string }) =>
    authedClient.request<components['schemas']['ReopenResponse']>(
      `/v1/admin/kyc/cases/${caseId}/reopen`,
      { method: 'POST', body: { reason } },
    ),
  );
}

export function useRecordReferenceOutcome() {
  return useKycMutation(
    ({ referenceId, outcome, notes }: { referenceId: number; outcome: string; notes?: string }) =>
      authedClient.request<components['schemas']['ReferenceRow']>(
        `/v1/admin/kyc/references/${referenceId}/outcome`,
        { method: 'POST', body: { outcome, notes: notes || null } },
      ),
  );
}

export type BackgroundCheckRow = components['schemas']['BackgroundCheckRow'];

/** The vendor is checked against the approved list server-side; an empty list refuses every order. */
export function useOrderBackgroundCheck() {
  return useKycMutation(
    ({
      caseId,
      provider_name,
      check_scope,
      provider_reference,
    }: {
      caseId: number;
      provider_name: string;
      check_scope: string;
      provider_reference?: string;
    }) =>
      authedClient.request<BackgroundCheckRow>(`/v1/admin/kyc/cases/${caseId}/background-checks`, {
        method: 'POST',
        body: { provider_name, check_scope, provider_reference: provider_reference || null },
      }),
  );
}

/** A result is recorded once; the server refuses to overwrite a check that is no longer pending. */
export function useRecordBackgroundCheck() {
  return useKycMutation(
    ({
      checkId,
      result,
      valid_until,
      notes,
    }: {
      checkId: number;
      result: string;
      valid_until?: string;
      notes?: string;
    }) =>
      authedClient.request<BackgroundCheckRow>(`/v1/admin/kyc/background-checks/${checkId}`, {
        method: 'PATCH',
        body: { result, valid_until: valid_until || null, notes: notes || null },
      }),
  );
}

/**
 * The reject dialog's quick-pick reasons, live first then retired (doc 05-admin/06 §3). A pick
 * copies the body into the reason, so editing a template never rewrites a rejection already sent.
 */
export function useRejectionTemplates() {
  return useQuery({
    queryKey: keys.kyc.rejectionTemplates(),
    queryFn: () =>
      authedClient.request<components['schemas']['RejectionTemplateListResponse']>('/v1/admin/kyc/rejection-templates'),
  });
}

export function useCreateRejectionTemplate() {
  return useKycMutation((body: components['schemas']['RejectionTemplateIn']) =>
    authedClient.request<RejectionTemplate>('/v1/admin/kyc/rejection-templates', { method: 'POST', body }),
  );
}

/** Edit, reorder or retire (`is_active: false`) — there is no delete. */
export function useUpdateRejectionTemplate() {
  return useKycMutation(({ id, ...body }: RejectionTemplatePatch & { id: number }) =>
    authedClient.request<RejectionTemplate>(`/v1/admin/kyc/rejection-templates/${id}`, { method: 'PATCH', body }),
  );
}
